import { Link } from "react-router-dom";
import { Bot, Zap, MessageSquare, Workflow, Brain, Shield, ArrowRight, TrendingUp } from "lucide-react";
import Navbar from "../../components/Navbar";
import PageMeta from "../../components/PageMeta";
import Footer from "../../components/Footer";
import FloatingFAQ from "../../components/FloatingFAQ";
import ConsultationForm from "../../components/ConsultationForm";

const features = [
  { icon: MessageSquare, title: "AI Chatbots", description: "24/7 customer support chatbots that handle inquiries intelligently." },
  { icon: Workflow, title: "Process Automation", description: "Streamline repetitive tasks and workflows with smart automation." },
  { icon: Brain, title: "Machine Learning", description: "Custom ML models trained on your data for better decisions." },
  { icon: Zap, title: "Fast Integration", description: "Seamless integration with your existing systems and tools." },
  { icon: Shield, title: "Secure & Private", description: "Enterprise-grade security for your sensitive business data." },
  { icon: TrendingUp, title: "Scalable Solutions", description: "AI systems that grow with your business needs." }
];

const services = [
  { title: "AI Chatbots", description: "Intelligent conversational bots for customer service and sales.", price: "From $1,499" },
  { title: "Workflow Automation", description: "Automate repetitive tasks and business processes.", price: "From $999" },
  { title: "Custom AI Solutions", description: "Tailored AI models for your specific business needs.", price: "From $4,999" },
  { title: "AI Integration", description: "Connect AI capabilities to your existing systems.", price: "From $2,499" }
];

const aiProcess = [
  { step: "01", title: "Assessment", description: "We analyze your processes to identify automation opportunities." },
  { step: "02", title: "Design", description: "We design AI solutions tailored to your needs." },
  { step: "03", title: "Development", description: "We build and train AI models on your data." },
  { step: "04", title: "Deployment", description: "We deploy, monitor, and optimize your AI systems." }
];

const technologies = ["OpenAI", "Python", "TensorFlow", "LangChain", "Hugging Face", "Azure AI", "AWS ML", "Dialogflow", "Node.js", "FastAPI"];

export default function AIAutomation() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="AI Automation for Business | Averoan Technologies"
        description="Chatbots, process automation, and AI integration that save time and unlock growth."
      />
      <Navbar />

      <section className="pt-32 pb-16 relative" data-testid="ai-hero">
        <div className="hero-glow absolute top-0 right-0 opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl">
            <span className="tag mb-6">AI Automation</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              AI that works<br /><span style={{ color: '#D4FF00' }}>for you</span>
            </h1>
            <p className="text-base md:text-lg max-w-xl mb-8" style={{ color: '#A1A1AA' }}>
              Leverage artificial intelligence to automate processes, enhance customer experiences, and unlock new business possibilities.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#get-started" className="btn-primary" data-testid="ai-cta">Start Your Project <ArrowRight size={16} /></a>
              <Link to="/portfolio" className="btn-outline">View Our Work</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Why AI Automation</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Transform your business</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="card">
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}>
                    <feature.icon size={22} style={{ color: '#D4FF00' }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{feature.title}</h3>
                  <p className="text-sm" style={{ color: '#A1A1AA' }}>{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">What We Offer</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Our services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service) => (
              <div key={service.title} className="p-8 rounded-xl" style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{service.title}</h3>
                  <span style={{ color: '#D4FF00', fontSize: '14px', fontWeight: '600' }}>{service.price}</span>
                </div>
                <p style={{ color: '#A1A1AA' }}>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Our Process</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>How we work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aiProcess.map((item) => (
              <div key={item.step}>
                <div className="text-5xl font-bold mb-4" style={{ fontFamily: '"Clash Display", sans-serif', color: '#D4FF00' }}>{item.step}</div>
                <h3 className="text-xl font-bold mb-2 normal-case" style={{ fontFamily: '"Clash Display", sans-serif' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="tag mb-4">Tech Stack</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>Technologies we use</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech) => (
              <span key={tech} className="px-6 py-3 rounded-full text-sm" style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)', color: '#A1A1AA' }}>{tech}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="get-started" className="section-padding" style={{ background: '#18181B' }} data-testid="ai-consultation">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="tag mb-4">Get Started</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4" style={{ fontFamily: '"Clash Display", sans-serif' }}>
              Ready to <span style={{ color: '#D4FF00' }}>automate?</span>
            </h2>
            <p className="text-base md:text-lg mt-4" style={{ color: '#A1A1AA' }}>
              Tell us about your project and book a free consultation.
            </p>
          </div>
          <ConsultationForm preSelectedServiceId="ai" />
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
